import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { debounce, isString } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState, useEffect, useCallback, SyntheticEvent } from 'react';

import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Grid, Stack, useTheme, Checkbox, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import { useTranslate } from 'src/locales';
import { Category } from 'src/services/utils/utils.types';
import { useOrgTenant } from 'src/auth/hooks/useOrgTenant';
import { useAuthContext } from 'src/auth/hooks/useAuthContext';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { useLazySearchCategoriesQuery } from 'src/services/utils/utils.service';
import { hideConnectionModal } from 'src/store/slices/connections/connectionsSlice';
import { useMarkAllAsReadMutation } from 'src/services/notifications/notifications.service';
import {
  ConnectionSettings,
  ConnectionActionOption,
} from 'src/services/connections/connections.types';
import {
  useActionConnectionRequestMutation,
  useLazyGetConnectionRequestByIdQuery,
} from 'src/services/connections/connections.service';

import FormProvider from 'src/components/hook-form';
import RHFSwitch from 'src/components/hook-form/rhf-switch';
import QuantityInput from 'src/components/number-input/number-input';
import RHFAutocomplete from 'src/components/hook-form/rhf-autocomplete';

import { Tenant } from 'src/types/enums';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
};

export default function ConnectionActionModal({ open }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const tenant = useOrgTenant();

  const authContext = useAuthContext();

  const { t } = useTranslate();

  const theme = useTheme();

  const dispatch = useAppDispatch();

  const requestId = useAppSelector((state) => state.connectionsState.connectionRequestId);
  const requestNotificationId = useAppSelector(
    (state) => state.connectionsState.requestNotificationId
  );

  const [fetchConnectionRequest, { isLoading: requestLoading, currentData: connectionData }] =
    useLazyGetConnectionRequestByIdQuery();

  const [actionConnectionRequest, { isLoading: isRejectingConnection }] =
    useActionConnectionRequestMutation();

  const [markAsRead] = useMarkAllAsReadMutation();

  const [jobsLimit, setJobsLimit] = useState<number>(0);

  const [searchQuery, setSearchQuery] = useState('');

  const [searchCategories, { currentData: categoriesData }] = useLazySearchCategoriesQuery();

  useEffect(() => {
    if (requestId) {
      fetchConnectionRequest({
        organization_id: authContext.organization.id,
        request_id: requestId,
      });
    }
  }, [authContext.organization.id, fetchConnectionRequest, requestId]);

  const ActionConnectionSchema = Yup.object().shape({
    job_limit_applied: Yup.boolean(),
    jobs_limit: Yup.number().positive().integer(),
    specializations: Yup.array().of(Yup.object()),
  });

  const defaultValues = {
    job_limit_applied: false,
    jobs_limit: 0,
    specializations: [],
  };

  const methods = useForm({
    resolver: yupResolver(ActionConnectionSchema),
    defaultValues,
    mode: 'all',
  });

  const {
    reset,
    getValues,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const onChangeJobLimit = (event: FocusEvent | PointerEvent | KeyboardEvent, value: number) => {
    event.preventDefault();

    setJobsLimit(value);
    setValue('jobs_limit', value);
  };

  const onSelectSpecialization = (_event: SyntheticEvent, value: string[]) => {
    if (!value) return;

    setValue('specializations', [...value]);
  };

  const onClose = useCallback(() => {
    dispatch(hideConnectionModal());
  }, [dispatch]);

  const debounceSearchCategories = useMemo(
    () =>
      debounce((input: string) => {
        searchCategories({
          name: input,
          page: 1,
          per_page: 5,
        });
      }),
    [searchCategories]
  );

  useEffect(() => {
    if (!searchQuery) return;

    debounceSearchCategories(searchQuery);
  }, [debounceSearchCategories, searchQuery]);

  const onSearch = (_event: SyntheticEvent, value: string) => setSearchQuery(value);

  const onActionConnectionRequest = useCallback(
    async (action: ConnectionActionOption) => {
      const connectionSettings: ConnectionSettings | undefined =
        action === ConnectionActionOption.Accept
          ? {
              job_limit_applied: getValues('job_limit_applied') ?? false,
              jobs_limit: getValues('jobs_limit') ?? 0,
              specializations:
                (getValues('specializations') as Category[]).map((item) => item.id) ?? [],
            }
          : undefined;

      try {
        await actionConnectionRequest({
          connectionId: requestId as string,
          action: {
            action,
            connection_settings: connectionSettings,
          },
        }).unwrap();

        onClose();
        markAsRead({ notifications: [requestNotificationId as string] });
        enqueueSnackbar(t(`admin-connections.api.action.success-${action}`));
        reset();
      } catch (e) {
        console.error(e);

        enqueueSnackbar(t(`admin-connections.api.action.default_error`), { variant: 'error' });
      }
    },
    [
      actionConnectionRequest,
      enqueueSnackbar,
      getValues,
      markAsRead,
      onClose,
      requestId,
      requestNotificationId,
      reset,
      t,
    ]
  );

  const renderConnectionDetails = () => {
    const requestingTenant =
      tenant === Tenant.Client ? connectionData?.recruiter : connectionData?.client;

    return (
      <Grid container rowSpacing={1} sx={{ px: theme.spacing(1) }}>
        <Grid item xs={12} sm={12}>
          <Typography variant="subtitle1">
            {t('admin-connections.action_modal.company_name')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {requestingTenant?.company_name}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={12}>
          <Typography variant="subtitle1">
            {t('admin-connections.action_modal.company_desc')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {requestingTenant?.company_information}
          </Typography>
        </Grid>

        {connectionData?.settings_data?.job_limit_applied && (
          <Grid item xs={12} sm={12}>
            <Typography variant="subtitle1">
              {t('admin-connections.action_modal.jobs_limit')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {connectionData?.settings_data?.jobs_limit}
            </Typography>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderClientForm = (
    <Grid container>
      <Grid
        container
        xs={12}
        item
        rowSpacing={1}
        sx={{ px: theme.spacing(1), pt: theme.spacing(1) }}
      >
        <Grid item xs={12} sx={{ color: 'text.secondary' }}>
          {t('admin-connections.jobs_limit_form.limit')}
        </Grid>
        <Grid item xs={12} sx={{ typography: 'subtitle2' }}>
          <RHFSwitch label="Limit # of Jobs" name="job_limit_applied" />
        </Grid>
      </Grid>

      <Grid container item rowSpacing={1} sx={{ px: theme.spacing(1), pt: theme.spacing(1) }}>
        <Grid item xs={12} sm={12} sx={{ color: 'text.secondary' }}>
          {t('admin-connections.jobs_limit_form.count')}
        </Grid>
        <Grid
          item
          xs={12}
          sx={{ typography: 'subtitle2', px: theme.spacing(1), pt: theme.spacing(1) }}
        >
          <QuantityInput min={1} max={99} value={jobsLimit} onChange={onChangeJobLimit as any} />
        </Grid>
      </Grid>

      <Grid container item rowSpacing={1} sx={{ px: theme.spacing(1), pt: theme.spacing(1) }}>
        <Grid item xs={12} sx={{ color: 'text.secondary' }}>
          {t('admin-connections.jobs_limit_form.specialisations')}
        </Grid>
        <Grid item xs={12} sx={{ typography: 'subtitle2' }}>
          <RHFAutocomplete
            multiple
            fullWidth
            onInputChange={onSearch}
            disableCloseOnSelect
            name="specializations"
            placeholder="Start typing to search specialisations"
            options={categoriesData?.results ?? []}
            onChange={onSelectSpecialization as any}
            filterOptions={(x) => x}
            renderOption={(props, option: Category, { selected }) => (
              <li {...props}>
                <Stack direction="row">
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  <Typography variant="body1">{option.name}</Typography>
                </Stack>
              </li>
            )}
            getOptionLabel={(option: Category | string) =>
              isString(option) ? option : option.name
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionKey={(option) => (option as Category).id as string}
          />
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <FormProvider methods={methods}>
        <DialogTitle>{t('admin-connections.action_modal.title')}</DialogTitle>

        <DialogContent>
          <Alert variant="outlined" severity="info" sx={{ mb: 3 }}>
            {t(`admin-connections.action_modal.alert_${tenant}`)}
          </Alert>

          {renderConnectionDetails()}

          {tenant === Tenant.Client && renderClientForm}
        </DialogContent>

        <DialogActions>
          <LoadingButton
            variant="outlined"
            onClick={() => onActionConnectionRequest(ConnectionActionOption.Reject)}
            disabled={requestLoading}
            loading={isSubmitting || isRejectingConnection}
          >
            {t('common.decline')}
          </LoadingButton>

          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting || isRejectingConnection}
            onClick={() => onActionConnectionRequest(ConnectionActionOption.Accept)}
            disabled={requestLoading}
          >
            {t('common.accept')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
